import { FC, useMemo, useRef, useContext } from "react";

import { SxProps } from "@mui/material";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import useTheme from "@mui/styles/useTheme";

import { PageLoadingContext } from "../../providers/PageLoadingProvider";
import { ScrollProvider } from "../../providers/ScrollProvider";

import { TunnelLayoutProps } from "./types";

// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { PageLoader } from "@/components/Loaders/PageLoader";

export const TunnelLayout: FC<TunnelLayoutProps> = ({
  children,
  noScreenPadding = false,
  disableFullHeightOnMobile = false,
}) => {
  const theme = useTheme();
  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  const rootSx: SxProps = useMemo(
    () => ({
      backgroundColor: theme.palette.common.white,
      height: `100vh`,
      ["@supports (height: 100dvh)"]: {
        height: `100dvh`,
      },
      ...(disableFullHeightOnMobile ? { [theme.breakpoints.down("md")]: { height: "unset" } } : undefined),
    }),
    [disableFullHeightOnMobile, theme.breakpoints, theme.palette.common.white]
  );

  const containerStyle = useMemo(
    () => ({
      height: "100%",
      padding: noScreenPadding ? 0 : undefined,
    }),
    [noScreenPadding]
  );

  const { isLoading } = useContext(PageLoadingContext);
  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <>
      <Box sx={rootSx} ref={scrollableContainerRef} data-test="tunnel-layout">
        <ScrollProvider scrollableContainerRef={scrollableContainerRef}>
          {children && (
            <Container maxWidth={noScreenPadding ? false : "lg"} style={containerStyle}>
              {children}
            </Container>
          )}
        </ScrollProvider>
      </Box>
    </>
  );
};
